import React, { useState } from 'react';
import './VideoCard.css';
import { FaDownload, FaVideo, FaMusic, FaTimes } from 'react-icons/fa';
import Lottie from 'react-lottie';
import loadingAnimation from '../loading1.json'; // Ensure this path is correct
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const VideoCard = ({ videoData, onClose }) => {
  const [selectedCategory, setSelectedCategory] = useState('video');
  const [isLoadingHD, setIsLoadingHD] = useState(false);
  const [isLoadingSD, setIsLoadingSD] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);

  if (!videoData || !videoData.thumbnailUrl) {
    return null;
  }

  const { thumbnailUrl, links } = videoData;

  const isThumbnailValid = thumbnailUrl && (
    thumbnailUrl.startsWith('http://') || 
    thumbnailUrl.startsWith('https://') || 
    thumbnailUrl.startsWith('/')
  );

  if (!isThumbnailValid) {
    return null;
  }

  const downloadFile = (url, filename, setLoading) => {
    setLoading(true);
    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        const timestamp = new Date().getTime(); // Get the current timestamp
        a.href = blobUrl;
        a.download = `${filename}_${timestamp}.mp4`; // Append the timestamp to the filename
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
        setLoading(false);
        toast.success("Downloaded successfully 👌", {
          icon: "✅",
          style: {
            backgroundColor: "#1E1E1E",
            color: "#fff",
            padding: "10px",
            borderRadius: "8px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
            fontSize: '14px',
            marginTop: "5rem",
            marginRight: '20px'
          },
          progressStyle: {
            backgroundColor: "#4CAF50",
          },
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
        });
      })
      .catch(error => {
        console.error(error);
        setLoading(false);
        toast.error("Error during download", {
          icon: "❗",
          style: {
            backgroundColor: "#2b2b2b",
            color: "#fff",
            padding: "10px",
            borderRadius: "8px",
            border: "1px solid #e74c3c",
            fontSize: '14px',
            marginTop: "5rem",
            marginRight: '20px',
          },
          progressStyle: {
            background: "#e74c3c",
          },
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
        });
      });
  };

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: loadingAnimation,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  return (
    <div className='video-card-container'>
      <button className="close-btn">
        <img className="close-icon" src="/assets/close.png" alt="close" onClick={onClose}/>
      </button>

      <div className="video-card">
        {/* Thumbnail Section */}
        <div className="thumbnail-section">
          <img src={thumbnailUrl} alt="Video Thumbnail" className="video-thumbnail" />
        </div>

        {/* Content Section */}
        <div className="content-section">
          {/* Category Toggle Buttons */}
          <div className="format-toggle">
            <button
              className={`format-btn ${selectedCategory === 'video' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('video')}
            >
              <FaVideo />
              Video
            </button>
            <button
              className={`format-btn ${selectedCategory === 'audio' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('audio')}
            >
              <FaMusic />
              Audio
            </button>
          </div>

          {/* Download Options */}
          <div className="download-options">
            <div className="options-header">
              <span className='video-card-text'>File Type</span>
              <span className='video-card-text'>File Size</span>
              <span className='video-card-text'>Action</span>
            </div>

            {selectedCategory === 'video' && (
              <div className="options-list" data-category={selectedCategory}>
                {(links.hd_url || links.sd_url) ? (
                  <>
                    {links.hd_url && (
                      <div className="option-item">
                        <span className='video-type'>HD MP4 {links.hd_quality}</span>
                        <span className='video-type'>{links.hd_size_mbs}</span>
                        <div  className="download-button-1">
                        <button 
                          className={`download-button ${isLoadingHD ? 'loading' : ''}`}
                          onClick={() => downloadFile(links.hd_url, `${links.hd_quality,"hd_video"}`, setIsLoadingHD)}
                        >
                          {isLoadingHD ? (
                            <Lottie options={defaultOptions} height={40} width={100} />
                          ) : (
                            <>
                              <FaDownload /> Download
                            </>
                          )}
                        </button>
                        </div>
                      </div>
                    )}
                    {links.sd_url && (
                      <div className="option-item">
                        <span className='video-type'>SD MP4 {links.sd_quality}</span>
                        <span className='video-type'>{links.sd_size_mbs}</span>
                        <div  className="download-button-1">
                        <button 
                          className={`download-button ${isLoadingSD ? 'loading' : ''}`}
                          onClick={() => downloadFile(links.sd_url, `${links.sd_quality,"sd_video"}`, setIsLoadingSD)}
                        >
                          {isLoadingSD ? (
                            <Lottie options={defaultOptions} height={40} width={100} />
                          ) : (
                            <>
                              <FaDownload /> Download
                            </>
                          )}
                        </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="no-options-message">No video options available</p>
                )}
              </div>
            )}

            {selectedCategory === 'audio' && (
              <div className="options-list">
                {links.audio_url ? (
                  <div className="option-item">
                    <span className='video-type'>MP3 {links.audio_quality}</span>
                    <span className='video-type'>{links.audio_size_mbs}</span>
                    <div  className="download-button-1">
                    <button 
                      className={`download-button ${isLoadingAudio ? 'loading' : ''}`}
                      onClick={() => downloadFile(links.audio_url, 'audio.mp3', setIsLoadingAudio)}
                    >
                      {isLoadingAudio ? (
                        <Lottie options={defaultOptions} height={40} width={100} />
                      ) : (
                        <>
                          <FaDownload /> Download
                        </>
                      )}
                    </button>
                    </div>
                  </div>
                ) : (
                  <p className="no-options-message">No audio options available</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <ToastContainer
        className="toast-container hide-toast"
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default VideoCard;
